/**
 * Structured logging for discipline rule engine
 *
 * Provides different log levels with clear prefixes to reduce console noise
 * while maintaining visibility of critical rule engine decisions.
 */

// Log level configuration
const LOG_LEVELS = {
  CRITICAL: '🚨 CRITICAL',  // Rule triggers, threshold met, consequences created
  WARN: '⚠️ WARN',          // Warnings and potential issues
  INFO: '📊 INFO',          // Rule evaluations, counts
  DEBUG: '🔍 DEBUG',        // Detailed data inspection
  TRACE: '📍 TRACE'         // Function entry/exit
};

// Category configuration - allows filtering by rule type/system
const LOG_CATEGORIES = {
  mentions: { enabled: true, label: 'Mentions', emoji: '📝' },
  mentionRules: { enabled: true, label: 'Mention Rules', emoji: '⚡' },
  thresholds: { enabled: true, label: 'Thresholds', emoji: '📊' },
  escalations: { enabled: true, label: 'Escalations', emoji: '⬆️' },
  accountability: { enabled: true, label: 'Accountability', emoji: '⚖️' },
  zeroTolerance: { enabled: true, label: 'Zero Tolerance', emoji: '🛑' },
  consequences: { enabled: true, label: 'Consequences', emoji: '🎯' },
  contracts: { enabled: true, label: 'Contracts', emoji: '📋' }
};

// Load category settings from localStorage
const loadCategorySettings = () => {
  try {
    const saved = localStorage.getItem('disciplineLogCategories');
    if (saved) {
      const parsed = JSON.parse(saved);
      Object.keys(parsed).forEach(key => {
        if (LOG_CATEGORIES[key]) {
          LOG_CATEGORIES[key].enabled = parsed[key];
        }
      });
    }
  } catch (e) {
    console.error('Failed to load log category settings:', e);
  }
};

// Save category settings to localStorage
const saveCategorySettings = () => {
  try {
    const settings = {};
    Object.keys(LOG_CATEGORIES).forEach(key => {
      settings[key] = LOG_CATEGORIES[key].enabled;
    });
    localStorage.setItem('disciplineLogCategories', JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save log category settings:', e);
  }
};

// Initialize from localStorage
loadCategorySettings();

// Enable/disable specific log levels
// Set to false to silence that level entirely
// INFO/DEBUG/TRACE are disabled by default to reduce console noise
// Use the DevTools UI in Quick Issue Dashboard to enable categories
const LOG_ENABLED = {
  critical: true,  // Always show critical events
  warn: true,      // Show warnings (potential issues)
  info: false,     // Hide rule evaluation summaries (enable via DevTools)
  debug: false,    // Hide detailed data inspection (enable via DevTools)
  trace: false     // Hide function entry/exit (enable via DevTools)
};

/**
 * Check if a category is enabled for logging
 */
const isCategoryEnabled = (category) => {
  if (!category) return true; // No category = always log
  return LOG_CATEGORIES[category]?.enabled !== false;
};

/**
 * Structured logger with toggleable levels and categories
 */
export const disciplineLogger = {
  /**
   * Critical events: rule triggers, consequences created, escalations
   */
  critical: (msg, data, category = null) => {
    if (LOG_ENABLED.critical && isCategoryEnabled(category)) {
      const prefix = category && LOG_CATEGORIES[category]
        ? `${LOG_CATEGORIES[category].emoji} ${LOG_LEVELS.CRITICAL}`
        : LOG_LEVELS.CRITICAL;
      if (data !== undefined) {
        console.log(`${prefix}: ${msg}`, data);
      } else {
        console.log(`${prefix}: ${msg}`);
      }
    }
  },

  /**
   * Warning events: potential issues, missing data, unexpected states
   */
  warn: (msg, data, category = null) => {
    if (LOG_ENABLED.warn && isCategoryEnabled(category)) {
      const prefix = category && LOG_CATEGORIES[category]
        ? `${LOG_CATEGORIES[category].emoji} ${LOG_LEVELS.WARN}`
        : LOG_LEVELS.WARN;
      if (data !== undefined) {
        console.warn(`${prefix}: ${msg}`, data);
      } else {
        console.warn(`${prefix}: ${msg}`);
      }
    }
  },

  /**
   * Info events: rule evaluations, counts, summaries
   */
  info: (msg, data, category = null) => {
    if (LOG_ENABLED.info && isCategoryEnabled(category)) {
      const prefix = category && LOG_CATEGORIES[category]
        ? `${LOG_CATEGORIES[category].emoji} ${LOG_LEVELS.INFO}`
        : LOG_LEVELS.INFO;
      if (data !== undefined) {
        console.log(`${prefix}: ${msg}`, data);
      } else {
        console.log(`${prefix}: ${msg}`);
      }
    }
  },

  /**
   * Debug events: detailed data inspection
   */
  debug: (msg, data, category = null) => {
    if (LOG_ENABLED.debug && isCategoryEnabled(category)) {
      const prefix = category && LOG_CATEGORIES[category]
        ? `${LOG_CATEGORIES[category].emoji} ${LOG_LEVELS.DEBUG}`
        : LOG_LEVELS.DEBUG;
      if (data !== undefined) {
        console.log(`${prefix}: ${msg}`, data);
      } else {
        console.log(`${prefix}: ${msg}`);
      }
    }
  },

  /**
   * Error events: failures, exceptions
   */
  error: (msg, data, category = null) => {
    if (LOG_ENABLED.critical && isCategoryEnabled(category)) {
      const prefix = category && LOG_CATEGORIES[category]
        ? `${LOG_CATEGORIES[category].emoji} ❌ ERROR`
        : '❌ ERROR';
      if (data !== undefined) {
        console.error(`${prefix}: ${msg}`, data);
      } else {
        console.error(`${prefix}: ${msg}`);
      }
    }
  },

  /**
   * Trace events: function entry/exit
   */
  trace: (msg, data, category = null) => {
    if (LOG_ENABLED.trace && isCategoryEnabled(category)) {
      const prefix = category && LOG_CATEGORIES[category]
        ? `${LOG_CATEGORIES[category].emoji} ${LOG_LEVELS.TRACE}`
        : LOG_LEVELS.TRACE;
      if (data !== undefined) {
        console.log(`${prefix}: ${msg}`, data);
      } else {
        console.log(`${prefix}: ${msg}`);
      }
    }
  }
};

/**
 * Helper to enable/disable log levels at runtime
 */
export const configureLogging = (config) => {
  Object.assign(LOG_ENABLED, config);
};

/**
 * Get current logging configuration
 */
export const getLoggingConfig = () => ({ ...LOG_ENABLED });

/**
 * Enable/disable specific log categories
 */
export const configureLogCategories = (categoryConfig) => {
  Object.keys(categoryConfig).forEach(key => {
    if (LOG_CATEGORIES[key]) {
      LOG_CATEGORIES[key].enabled = categoryConfig[key];
    }
  });
  saveCategorySettings();
};

/**
 * Toggle a single category
 */
export const toggleLogCategory = (category, enabled) => {
  if (LOG_CATEGORIES[category]) {
    LOG_CATEGORIES[category].enabled = enabled;
    saveCategorySettings();
  }
};

/**
 * Get all log categories
 */
export const getLogCategories = () => {
  const categories = {};
  Object.keys(LOG_CATEGORIES).forEach(key => {
    categories[key] = {
      ...LOG_CATEGORIES[key]
    };
  });
  return categories;
};
